import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import { CheckCircle2, AlertCircle, Trash2, ExternalLink } from 'lucide-react';
import {
  api,
  type VendorQuoteIntakeDetailResponse,
  type VendorQuoteIntakeParsedQuote,
  type VendorQuoteIntakeCandidateLine,
  type VendorQuoteIntakeTriageResponse,
} from '@/lib/api';
import { INTAKE_STATUS_META } from '@/components/vendor-inbox-list';

interface Props {
  publicId: string;
}

function fmtMoney(unitPrice: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(unitPrice);
  } catch {
    return `${unitPrice} ${currency}`;
  }
}

export function VendorInboxDetail({ publicId }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [discardReason, setDiscardReason] = useState('');
  const [showDiscard, setShowDiscard] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['vendor-quote-intake', publicId],
    queryFn: () =>
      api<VendorQuoteIntakeDetailResponse>(`/api/vendor-quote-intake/${publicId}`, {
        tokenGetter: () => getToken(),
      }),
  });

  const triage = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<VendorQuoteIntakeTriageResponse>(`/api/vendor-quote-intake/${publicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setActionError(null);
      setShowDiscard(false);
      qc.invalidateQueries({ queryKey: ['vendor-quote-intake'] });
    },
    onError: (e: { message?: string }) => setActionError(e?.message ?? 'Action failed'),
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (isError || !data) {
    return (
      <div className="p-6 text-sm text-destructive">
        {(error as { message?: string })?.message ?? 'Failed to load intake'}
      </div>
    );
  }

  const { intake, candidateLines } = data;
  const meta = INTAKE_STATUS_META[intake.status];
  const parsed = intake.parsedJson;
  const isTerminal = intake.status === 'manually_committed' || intake.status === 'discarded';
  // v0.5 limitation: assigning from the inbox commits ONE quote and closes the
  // whole intake. That's correct for single-quote emails but would strand the
  // other quotes on a multi-quote email — so for those we route the broker to
  // the existing parser modal (which commits multiple quotes properly) instead
  // of showing per-quote Assign buttons that only the first click would honor.
  const isMultiQuote = (parsed?.quotes.length ?? 0) > 1;
  const canAssignHere =
    !isTerminal && !isMultiQuote && (intake.status === 'ambiguous' || intake.status === 'unmatched');

  // Resolve candidate line publicIds for a given quote index.
  const candidateIdsForQuote = (quoteIndex: number, pn: string): string[] => {
    const raw = intake.candidateLineItemIds;
    if (Array.isArray(raw)) return raw; // single-quote case
    if (raw && typeof raw === 'object') return raw[pn] ?? [];
    return [];
  };

  return (
    <div className="h-full overflow-auto p-4 lg:p-6 space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg font-semibold">{parsed?.vendor?.name ?? intake.senderName ?? 'Vendor quote'}</h2>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${meta.className}`}>{meta.label}</span>
        </div>
        <div className="text-sm text-foreground-muted mt-1">
          {intake.subject ?? '(no subject)'}
        </div>
        <div className="text-xs text-foreground-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {intake.senderEmail && <span>From: {intake.senderEmail}</span>}
          {intake.parserConfidence != null && <span>Parser confidence: {(intake.parserConfidence * 100).toFixed(0)}%</span>}
          <span>Received: {new Date(intake.receivedAt ?? intake.createdAt).toLocaleString()}</span>
        </div>
      </div>

      {actionError && (
        <div className="flex items-start gap-2 p-3 rounded bg-red-50 text-red-800 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {/* Terminal-state banner */}
      {intake.status === 'manually_committed' && (
        <div className="flex items-center gap-2 p-3 rounded bg-emerald-50 text-emerald-800 text-sm">
          <CheckCircle2 className="w-4 h-4" />
          <span>Committed to a line item{intake.triagedAt ? ` on ${new Date(intake.triagedAt).toLocaleDateString()}` : ''}.</span>
        </div>
      )}
      {intake.status === 'discarded' && (
        <div className="p-3 rounded bg-gray-50 text-gray-600 text-sm">
          Discarded{intake.discardReason ? `: ${intake.discardReason}` : ''}.
        </div>
      )}
      {intake.status === 'auto_committed' && (
        <div className="flex items-center justify-between gap-3 p-3 rounded bg-emerald-50 text-emerald-800 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span>Auto-committed to an open line (review pending).</span>
          </div>
          <button
            className="btn-primary text-xs"
            disabled={triage.isPending}
            onClick={() => triage.mutate({ markReviewed: true })}
          >
            Mark reviewed
          </button>
        </div>
      )}

      {/* Parse-failed: raw email is all we have */}
      {intake.status === 'parse_failed' && (
        <div className="p-3 rounded bg-red-50 text-red-800 text-sm">
          The parser couldn't extract any quotes from this email. Review the raw text
          below — paste it into the vendor-quote parser to handle manually, or discard.
        </div>
      )}

      {/* Parsed quotes + per-quote triage */}
      {parsed && parsed.quotes.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wide">
            Parsed {parsed.quotes.length === 1 ? 'quote' : `quotes (${parsed.quotes.length})`}
          </h3>
          {isMultiQuote && !isTerminal && (
            <div className="p-3 rounded bg-blue-50 text-blue-800 text-sm">
              This email contains {parsed.quotes.length} quotes. Commit them with the
              vendor-quote parser (it handles multi-quote emails line by line) — copy the
              raw email below into the parser, or discard if it's noise.
            </div>
          )}
          {parsed.quotes.map((q, idx) => (
            <QuoteCard
              key={idx}
              quote={q}
              candidates={candidateIdsForQuote(idx, q.part_number)
                .map((id) => candidateLines[id])
                .filter((c): c is VendorQuoteIntakeCandidateLine => Boolean(c))}
              canAssign={canAssignHere}
              assigning={triage.isPending}
              onAssign={(lineId) =>
                triage.mutate({ assignToLine: { rfqLineItemPublicId: lineId, quoteIndex: idx } })
              }
            />
          ))}
        </div>
      )}

      {/* Discard control — available for any non-terminal, non-auto status */}
      {!isTerminal && intake.status !== 'auto_committed' && (
        <div className="border-t border-border pt-4">
          {!showDiscard ? (
            <button
              className="btn-ghost text-sm text-foreground-muted"
              onClick={() => setShowDiscard(true)}
            >
              <Trash2 className="w-4 h-4" />
              Discard this intake
            </button>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Discard reason</label>
              <input
                value={discardReason}
                onChange={(e) => setDiscardReason(e.target.value)}
                placeholder="e.g. Not a real quote / duplicate / spam"
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              />
              <div className="flex gap-2">
                <button
                  className="btn-primary text-sm"
                  disabled={!discardReason.trim() || triage.isPending}
                  onClick={() => triage.mutate({ discard: { reason: discardReason.trim() } })}
                >
                  Confirm discard
                </button>
                <button className="btn-ghost text-sm" onClick={() => setShowDiscard(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Raw email — always available, collapsed by default */}
      <details className="border-t border-border pt-4">
        <summary className="text-sm font-medium cursor-pointer text-foreground-muted">
          Raw email source
        </summary>
        <div className="mt-2 flex items-center justify-end">
          <button
            className="btn-ghost text-xs"
            onClick={() => navigator.clipboard?.writeText(intake.rawText)}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Copy for parser
          </button>
        </div>
        <pre className="mt-1 p-3 bg-muted rounded text-xs whitespace-pre-wrap max-h-96 overflow-auto">
          {intake.rawText}
        </pre>
      </details>

      {parsed?.warnings && parsed.warnings.length > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 rounded p-3">
          <div className="font-medium mb-1">Parser warnings</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function QuoteCard({
  quote,
  candidates,
  canAssign,
  assigning,
  onAssign,
}: {
  quote: VendorQuoteIntakeParsedQuote;
  candidates: VendorQuoteIntakeCandidateLine[];
  canAssign: boolean;
  assigning: boolean;
  onAssign: (lineId: string) => void;
}) {
  return (
    <div className="border border-border rounded-lg p-3 bg-background">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-mono font-semibold text-sm">{quote.part_number}</div>
        <div className="text-sm font-semibold">
          {fmtMoney(quote.unit_price, quote.currency)} <span className="text-foreground-muted font-normal">/ ea × {quote.qty_offered}</span>
        </div>
      </div>
      <div className="text-xs text-foreground-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        {quote.condition_offered && <span>Cond: {quote.condition_offered}</span>}
        {quote.cert_offered && <span>Cert: {quote.cert_offered}</span>}
        {quote.lead_time_days != null && <span>Lead: {quote.lead_time_days}d</span>}
        {quote.incoterms && <span>{quote.incoterms}{quote.fob_location ? ` ${quote.fob_location}` : ''}</span>}
        {quote.country_of_origin && <span>COO: {quote.country_of_origin}</span>}
      </div>

      {/* Candidate lines to assign to */}
      <div className="mt-3">
        {candidates.length === 0 ? (
          <div className="text-xs text-foreground-muted italic">
            No open line matched this part number. Discard, or paste the raw email into
            the vendor-quote parser to attach it manually.
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-foreground-muted">
              {canAssign ? 'Assign to:' : 'Candidate lines:'}
            </div>
            {candidates.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-2 p-2 rounded border border-border text-xs"
              >
                <div className="min-w-0">
                  <Link href={`/rfqs/${c.rfqPublicId}`} className="font-medium text-primary hover:underline">
                    {c.rfqRef}
                  </Link>{' '}
                  <span className="font-mono">{c.partNumber}</span>
                  <span className="text-foreground-muted">
                    {' '}· {c.customerName ?? 'Unknown customer'} · qty {c.qty} · {c.stage}
                  </span>
                  {!c.isOpen && <span className="text-amber-700"> · closed</span>}
                </div>
                {canAssign && (
                  <button
                    className="btn-primary text-xs flex-shrink-0"
                    disabled={assigning || !c.isOpen}
                    onClick={() => onAssign(c.id)}
                  >
                    Assign
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
